import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import styles from "./AppCollections.module.scss";
import { useFetchAppCollectionsMutation } from "@/features/auth/api/authAPI";
import Loader from "@/shared/ui/Loader/Loader";
import ModalAppsEmpty from "@/shared/ui/ModalAppsEmpty/ModalAppsEmpty";
import CollectionCard from "./CollectionCard";
import { AppCollection } from "@/features/auth/types/auth";

interface OutletContext {
  refreshCollections: number;
  handleRefreshCollections: () => void;
  selectedCollections: Set<number>;
  setSelectedCollections: React.Dispatch<React.SetStateAction<Set<number>>>;
}

const AppCollections = () => {
  const [fetchAppCollections] = useFetchAppCollectionsMutation();
  const [collections, setCollections] = useState<AppCollection[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Get refresh trigger and selection state from outlet context
  const { refreshCollections, selectedCollections, setSelectedCollections } =
    useOutletContext<OutletContext>();

  const loadCollections = async (isRefresh = false) => {
    try {
      if (!isRefresh) {
        setIsInitialLoading(true);
      }

      const res = await fetchAppCollections();
      setCollections(res.data?.results || []);
    } catch (error) {
      console.error("Failed to fetch collections:", error);
    } finally {
      if (!isRefresh) {
        setIsInitialLoading(false);
      }
    }
  };

  const handleCollectionSelect = (id: number) => {
    setSelectedCollections((prevSelected) => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        newSelected.add(id);
      }
      return newSelected;
    });
  };

  useEffect(() => {
    loadCollections();
  }, []);

  // Refresh collections when refreshCollections changes
  useEffect(() => {
    if (refreshCollections > 0) {
      loadCollections(true); // Pass true to indicate this is a refresh
    }
  }, [refreshCollections]);

  return (
    <div className={styles.container}>
      {isInitialLoading ? (
        <div className={styles.loading}>
          <Loader size={40} />
        </div>
      ) : collections?.length && collections?.length > 0 ? (
        <div className={styles.collectionsContainer}>
          {collections?.map((collection) => (
            <CollectionCard
              key={collection.id}
              collection={collection}
              isSelected={selectedCollections.has(collection.id)}
              onSelect={handleCollectionSelect}
            />
          ))}
        </div>
      ) : (
        <div className={styles.emptyContainer}>
          <ModalAppsEmpty
            text="No collections added"
            img="/img/screens-black-icon.svg"
          />
        </div>
      )}
    </div>
  );
};

export default AppCollections;
